/**
 * Folding a freshly fetched page of a thread into the one on screen.
 *
 * The 5-second poll asks for the NEWEST page and nothing else. Replacing the
 * list with it threw away everything the reader had paged in by scrolling up —
 * and because the load-more trigger re-derives its cursor from the first
 * message still in the list, the thread then re-requested the same page every
 * five seconds. Merging keeps what is already held.
 */

/** A message's identity. The API emits it as a string; a local echo carries its own. */
export const messageKey = (m) => {
  const id = m?.id;
  if (id && typeof id === 'object') return id._serialized || null;
  return id == null || id === '' ? null : String(id);
};

/**
 * Merge `incoming` over `existing`, keeping every message either side holds and
 * letting the server's copy win — it carries the delivery state a local echo
 * cannot know.
 */
export function mergeMessagePage(existing, incoming) {
  const byKey = new Map();
  const anonymous = [];

  const add = (m, wins) => {
    if (!m) return;
    const key = messageKey(m);
    if (!key) {
      anonymous.push(m);
      return;
    }
    const prev = byKey.get(key);
    byKey.set(key, prev && !wins ? prev : {...prev, ...m});
  };

  (existing || []).forEach((m) => add(m, false));
  (incoming || []).forEach((m) => add(m, true));

  return [...byKey.values(), ...anonymous].sort(
    (a, b) => (a.timestamp || 0) - (b.timestamp || 0),
  );
}

/**
 * Append a message the user just sent, unless the server has already told us
 * about it. Without this the reply sits twice in the thread until the next poll.
 */
export function appendOwnMessage(existing, message) {
  const key = messageKey(message);
  if (key && (existing || []).some((m) => messageKey(m) === key)) {
    return existing;
  }
  return [...(existing || []), message];
}

/**
 * Keep a conversation the user has read looking read.
 *
 * Opening a chat clears its badge locally and tells WhatsApp it was seen, but
 * WhatsApp keeps reporting it unread for a while — so the next refresh of the
 * list brought the old count straight back and the badge reappeared on a
 * conversation the user had just finished reading.
 *
 * `readAt` records the time of the newest message each chat held when it was
 * opened. A count is suppressed only while nothing newer has arrived, so a
 * genuinely new message still shows up.
 */
export function applyLocalReads(conversations, readAt) {
  if (!readAt || !readAt.size) return conversations || [];
  return (conversations || []).map((c) => {
    const at = readAt.get(c?.chatId);
    if (at === undefined || !c?.unreadCount) return c;
    const newest = Number(c.lastMessage?.timestamp) || 0;
    return newest > at ? c : {...c, unreadCount: 0};
  });
}

/** The moment a conversation was read: the time of its newest message. */
export function readMarkFor(conversation) {
  return Number(conversation?.lastMessage?.timestamp) || 0;
}
